// Centralized API client for all catalogue health endpoints.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/seller-intelligence/catalogue-health`;

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

// Throws AmazonValidationError if Amazon rejects with structured issues; throws Error otherwise.
export class AmazonValidationError extends Error {
  constructor(issues) {
    super('Amazon validation failed');
    this.amazonIssues = issues;
  }
}

const call = async (url, options) => {
  const res = await fetch(url, options);
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || data.message || `Request failed (${res.status})`);
  return data.data ?? data;
};

export const getProductCounts = async (token, { page = 1, perPage = 10, forceRefresh = false } = {}) => {
  const params = new URLSearchParams({ page, per_page: perPage });
  if (forceRefresh) params.set('refresh', '1');
  const res = await fetch(`${BASE}/product-counts?${params}`, { headers: authHeaders(token) });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || data.message || 'Failed to fetch product counts');
  return data;
};

export const getHealthData = (token) =>
  call(`${BASE}/data`, { headers: authHeaders(token) });

export const getFixes = (token) =>
  call(`${BASE}/fixes`, { headers: authHeaders(token) });

export const generateFix = (token, payload) =>
  call(`${BASE}/generate-fix`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const applyFix = async (token, payload) => {
  const res = await fetch(`${BASE}/apply-fix`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    if (data.amazon_issues?.length > 0) throw new AmazonValidationError(data.amazon_issues);
    throw new Error(data.error || 'Amazon rejected the fix');
  }
  return data;
};

export const getFixStatus = (token, sku, issueCode) =>
  call(`${BASE}/fix-status`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ sku, issue_code: issueCode }),
  });

export const dismissFix = (token, fixId) =>
  call(`${BASE}/dismiss-fix`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ fix_id: fixId }),
  });

export const deleteFix = (token, fixId) =>
  call(`${BASE}/delete-fix`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ fix_id: fixId }),
  });

export const getIssueAnalysis = (token, payload) =>
  call(`${BASE}/issue-analysis`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

// Auth endpoint — gets the Amazon OAuth URL for connecting a seller account.
export const initiateAmazonLogin = async (token) => {
  const res = await fetch(`${API_URL}/auth/amazon/login`, {
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Login failed: ${res.status}`);
  }
  return res.json();
};
